import Accordion from '@/common/components/Accordion'
import Skeleton from '@/common/components/Skeleton'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { UpdateBillDetailsModal } from '@/features/privateCompanies/components/updateContract/UpdateBillDetailsModal'
import { UpdateClosingDateModal } from '@/features/privateCompanies/components/updateContract/UpdateContractClosingDateModal'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { CompanyContractClosingDateType } from '@/features/privateCompanies/types/companyContractClosingDate.type'
import { strategy as billGroupingModeStrategies } from '@/features/privateCompanyLeadsFunnel/constants/billGroupingModeOptions.constant'
import { strategy as billSubGroupingModeStrategies } from '@/features/privateCompanyLeadsFunnel/constants/billSubGroupingModeOptions.constant'
import { strategy as cycleStrategies } from '@/features/privateCompanyLeadsFunnel/constants/cycleOptions.constant'
import { options as defaultContractCnpjOptions } from '@/features/privateCompanyLeadsFunnel/constants/defaultContractCnpjOptions.constant'
import { strategy as paymentMethodStrategies } from '@/features/privateCompanyLeadsFunnel/constants/paymentMethodOptions.constant'
import { options as weekDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/weekDaysOptions.constants'
import { strategy as billByStrategies } from "@/features/privateCompanyLeadsFunnel/constants/billByOptions.constant"
import { BinaryIcon, CalendarDaysIcon, CircleChevronDownIcon } from 'lucide-react'


type BillDetailsProps = {
  contract?: CompanyContractType
  contractClosingDate?: CompanyContractClosingDateType
  companyKind?: string
}

export const BillDetails = ({ contract, contractClosingDate, companyKind }: BillDetailsProps) => {
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToShowContractClosingDate = hasUserPermission('Admin::Client::Contracts::ShowClosingDay')

  return (
    <Accordion
      defaultOpen
      name="bill_details"
      head={
        <div className="flex items-center gap-x-2">
          <div onClick={(e) => e.stopPropagation()}>
            <UpdateBillDetailsModal contract={contract} companyKind={companyKind} />
          </div>

          <p className="text-white-800">Detalhes da Fatura</p>
        </div>
      }
      triggerClassName="py-0"
      bodyClassName="pt-4 pb-0"
      body={
        <div className="flex flex-col gap-y-4">
          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">Ciclo de fatura:</p>

            <div className="flex items-center gap-x-1">
              <CircleChevronDownIcon strokeWidth={1} className="size-5 text-white-400" />

              <p data-testid="contract_cycle" className="text-xs text-white-400">
                {contract?.cycle ? cycleStrategies[contract.cycle as keyof typeof cycleStrategies] : '--'}
              </p>
            </div>
          </div>

          {contract?.cycle === 'fixed' && contract?.cycle_fixed_interval && (
            <div className="flex items-center gap-x-2">
              <p className="text-[11px] leading-3 text-white-800">Intervalo de ciclo fixo:</p>

              <div className="flex items-center gap-x-1">
                <BinaryIcon strokeWidth={1} className="size-5 text-white-400" />

                <p data-testid="contract_cycle_fixed_interval" className="text-xs text-white-400">
                  {contract?.cycle_fixed_interval}
                </p>
              </div>
            </div>
          )}

          {contract?.cycle !== 'fixed' && (
            <div className="flex items-center gap-x-2">
              <p className="text-[11px] leading-3 text-white-800">Dia de abertura:</p>

              <div className="flex items-center gap-x-1">
                <BinaryIcon strokeWidth={1} className="size-5 text-white-400" />

                <p data-testid="contract_cycle_start_day" className="text-xs text-white-400">
                  {contract?.cycle === 'weekly'
                    ? contract?.cycle_start_day !== null && contract?.cycle_start_day !== undefined
                      ? weekDaysOptions[contract?.cycle_start_day].label
                      : undefined
                    : contract?.cycle_start_day}
                </p>
              </div>
            </div>
          )}

          {isUserPermissionsPending ? (
            <Skeleton className="w-72 h-6" />
          ) : userHasPermissionToShowContractClosingDate ? (
            <div className="flex items-center gap-x-2">
              <p className="text-[11px] leading-3 text-white-800">Fechamento da próxima fatura:</p>

              <div className="flex items-center gap-x-1">
                <CalendarDaysIcon strokeWidth={1} className="size-5 text-white-400" />

                <p data-testid="contract_next_closing_date" className="text-xs text-white-400">
                  {contractClosingDate?.next_closing_date
                    ? dateTransformer(contractClosingDate.next_closing_date)
                    : '--'}
                </p>

                <UpdateClosingDateModal contractId={contract?.id} contractClosingDate={contractClosingDate} />
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">Vencimento da fatura:</p>

            <div className="flex items-center gap-x-1">
              <BinaryIcon strokeWidth={1} className="size-5 text-white-400" />

              <p data-testid="contract_closing_to_due_in_days" className="text-xs text-white-400">
                {contract?.closing_to_due_in_days ? `D+${contract.closing_to_due_in_days}` : '--'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">Modo de agrupamento da Fatura:</p>

            <div data-testid="contract_bill_grouping_mode" className="flex items-center gap-x-1">
              <CircleChevronDownIcon strokeWidth={1} className="size-5 text-white-400" />

              <p className="text-xs text-white-400">
                {contract?.bill_grouping_mode
                  ? billGroupingModeStrategies[contract.bill_grouping_mode as keyof typeof billGroupingModeStrategies]
                  : '--'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">Modo de subagrupamento da Fatura:</p>

            <div data-testid="contract_bill_sub_grouping_mode" className="flex items-center gap-x-1">
              <CircleChevronDownIcon strokeWidth={1} className="size-5 text-white-400" />

              <p className="text-xs text-white-400">
                {contract?.bill_sub_grouping_mode
                  ? billSubGroupingModeStrategies[contract.bill_sub_grouping_mode as keyof typeof billSubGroupingModeStrategies]
                  : '--'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">Segmentação da Fatura:</p>

            <div data-testid="contract_bill_by" className="flex items-center gap-x-1">
              <CircleChevronDownIcon strokeWidth={1} className="size-5 text-white-400" />

              <p className="text-xs text-white-400">
                {contract?.bill_by
                  ? billByStrategies[contract.bill_by as keyof typeof billByStrategies]
                  : '--'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">Modo Método de Pagamento:</p>

            <div className="flex items-center gap-x-1">
              <CircleChevronDownIcon strokeWidth={1} className="size-5 text-white-400" />

              <p data-testid="contract_payment_method" className="text-xs text-white-400">
                {contract?.payment_method
                  ? paymentMethodStrategies[contract.payment_method as keyof typeof paymentMethodStrategies]
                  : '--'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">Dias após vencimento até inadimplência:</p>

            <div className="flex items-center gap-x-1">
              <BinaryIcon strokeWidth={1} className="size-5 text-white-400" />

              <p data-testid="contract_due_to_suspension_in_days" className="text-xs text-white-400">
                {contract?.due_to_suspension_in_days ? `D+${contract.due_to_suspension_in_days}` : '--'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            <p className="text-[11px] leading-3 text-white-800">CNPJ emissor de nota da fatura:</p>

            <div className="flex items-center gap-x-1">
              <CircleChevronDownIcon strokeWidth={1} className="size-5 text-white-400" />

              <p data-testid="contract_cnpj_for_fiscal_document" className="text-xs text-white-400">
                {contract?.cnpj_for_fiscal_document
                  ? defaultContractCnpjOptions.find((option) => option.value === contract.cnpj_for_fiscal_document)
                    ?.label
                  : '--'}
              </p>
            </div>
          </div>
        </div>
      }
    />
  )
}
